import Lenis from "lenis";
import { StatsLineChart, StatsLineChartDataKey } from "./StatsLineChart";

const DESKTOP_CROSSHAIR_INDICES = [
  [0, 1, 3, 4],
  [1, 2, 4, 5],
  [3, 4, 6, 7],
  [4, 5, 7, 8],
  [6, 7, 9, 10],
  [7, 8, 10, 11],
];

const MOBILE_CROSSHAIR_INDICES = [
  [0, 1, 2, 3],
  [2, 3, 4, 5],
  [4, 5, 6, 7],
  [6, 7, 8, 9],
  [8, 9, 10, 11],
  [10, 11, 12, 13],
];

type ContestFormProps = {
  lenis?: Lenis;
};

export class ContestForm {
  form: HTMLFormElement;
  inputElements: HTMLInputElement[];
  legalRow: HTMLDivElement;
  submitButton: HTMLButtonElement;

  errorResponseElement: HTMLDivElement;
  successResponseElement: HTMLDivElement;
  successResponseMessageElement: HTMLDivElement;

  desktopCrosshairSVGs: SVGSVGElement[];
  mobileCrosshairSVGs: SVGSVGElement[];

  isPristine = true;
  focusedInputIndex?: number;

  lenis?: Lenis;

  prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)")
    .matches;

  constructor({ lenis }: ContestFormProps) {
    this.lenis = lenis;

    this.form = document.querySelector("#contest-form") as HTMLFormElement;

    this.inputElements = Array.from(
      this.form.querySelectorAll(
        ".homepage-contest-form__row input, .homepage-contest-form__row select"
      )
    ) as HTMLInputElement[];

    this.legalRow = document.querySelector(
      "#contest-form-legal-row"
    ) as HTMLDivElement;

    this.submitButton = document.querySelector(
      "#contest-form-submit"
    ) as HTMLButtonElement;

    this.errorResponseElement = document.querySelector(
      "#contest-form-error-response"
    ) as HTMLDivElement;

    this.successResponseElement = document.querySelector(
      "#contest-form-success-response"
    ) as HTMLDivElement;

    this.successResponseMessageElement = document.querySelector(
      ".homepage-contest-form-success-response__content"
    ) as HTMLDivElement;

    this.desktopCrosshairSVGs = Array.from(
      this.form.querySelectorAll(".homepage-section-border--desktop svg")
    ) as SVGSVGElement[];

    this.mobileCrosshairSVGs = Array.from(
      this.form.querySelectorAll(".homepage-section-border--mobile svg")
    ) as SVGSVGElement[];

    this.init();
  }

  init() {
    this.form.addEventListener("input", this.onFormInput);
    this.form.addEventListener("submit", this.onFormSubmit);

    this.inputElements.forEach((input, index) => {
      input.addEventListener("focus", () => this.onInputFocus(index));

      if (!this.prefersReducedMotion) {
        input.addEventListener("blur", this.onInputBlur);

        input.addEventListener("mouseover", () => this.onInputMouseOver(index));

        input.addEventListener("mouseout", this.onInputMouseOut);
      }
    });
  }

  onInputFocus = (index: number) => {
    if (index <= DESKTOP_CROSSHAIR_INDICES.length - 1) {
      this.focusedInputIndex = index;
    }

    if (this.isPristine) {
      this.isPristine = false;

      this.showLegalRow();
    }

    if (this.prefersReducedMotion) {
      return;
    }

    this.setActiveInputCrosshairs(index);
  };

  onInputBlur = () => {
    this.focusedInputIndex = undefined;
    this.resetInputCrosshairs();
  };

  onInputMouseOver = (index: number) => {
    if (this.focusedInputIndex !== undefined) {
      return;
    }

    this.setActiveInputCrosshairs(index);
  };

  onInputMouseOut = () => {
    if (this.focusedInputIndex !== undefined) {
      return;
    }

    this.resetInputCrosshairs();
  };

  onFormInput = () => {
    const isValid = this.form.checkValidity();
    this.submitButton.toggleAttribute("disabled", !isValid);
  };

  handleFormResponse(data: { success: boolean; message: string }) {
    console.log("Fetched data:", data);

    if (data.success) {
      this.onFormSuccess(data.message);
    } else {
      this.onFormError(data.message);
    }
  }

  onFormSubmit = (event: Event) => {
    event.preventDefault();

    const formData = new FormData(this.form);
    const data = Object.fromEntries(formData.entries());

    this.errorResponseElement.classList.remove(
      "homepage-contest-form-error-response--active"
    );

    fetch(
      `${location.origin}/wp-content/themes/nba-represent/_inc/nba-submit-form.php`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }
    )
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
        return res.json();
      })
      .then((responseData) => this.handleFormResponse(responseData))
      .catch((err) => console.error("Fetch error:", err));
  };

  onFormError = (message: string) => {
    this.errorResponseElement.classList.add(
      "homepage-contest-form-error-response--active"
    );

    this.errorResponseElement.innerHTML = message;

    if (this.lenis) {
      this.lenis.scrollTo(this.errorResponseElement, {
        offset: -window.innerHeight / 2,
        immediate: this.prefersReducedMotion,
      });
    } else {
      this.errorResponseElement.scrollIntoView({
        behavior: this.prefersReducedMotion ? "auto" : "smooth",
        block: "center",
      });
    }
  };

  onFormSuccess = (message: string) => {
    this.form.remove();

    this.successResponseElement.classList.add(
      "homepage-contest-form-success-response--active"
    );

    this.successResponseMessageElement.innerHTML = message;

    if (this.lenis) {
      this.lenis.scrollTo(this.successResponseElement, {
        offset: -window.innerHeight / 2,
        immediate: this.prefersReducedMotion,
      });
    } else {
      this.successResponseElement.scrollIntoView({
        behavior: this.prefersReducedMotion ? "auto" : "smooth",
        block: "center",
      });
    }
  };

  setActiveInputCrosshairs(index: number) {
    if (index > DESKTOP_CROSSHAIR_INDICES.length - 1) {
      this.resetInputCrosshairs();
      return;
    }

    const desktopTopLeftIndex = DESKTOP_CROSSHAIR_INDICES[index][0];
    const desktopTopRightIndex = DESKTOP_CROSSHAIR_INDICES[index][1];
    const desktopBottomLeftIndex = DESKTOP_CROSSHAIR_INDICES[index][2];
    const desktopBottomRightIndex = DESKTOP_CROSSHAIR_INDICES[index][3];

    const mobileTopLeftIndex = MOBILE_CROSSHAIR_INDICES[index][0];
    const mobileTopRightIndex = MOBILE_CROSSHAIR_INDICES[index][1];
    const mobileBottomLeftIndex = MOBILE_CROSSHAIR_INDICES[index][2];
    const mobileBottomRightIndex = MOBILE_CROSSHAIR_INDICES[index][3];

    this.desktopCrosshairSVGs.forEach((svg, i) => {
      switch (i) {
        case desktopTopLeftIndex:
          svg.children[0].setAttribute(
            "style",
            "transform: translate(-5px, 0px)"
          );
          svg.children[1].setAttribute(
            "style",
            "transform: translate(0px, -5px)"
          );
          break;
        case desktopTopRightIndex:
          svg.children[0].setAttribute(
            "style",
            "transform: translate(5px, 0px)"
          );
          svg.children[1].setAttribute(
            "style",
            "transform: translate(0px, -5px)"
          );
          break;
        case desktopBottomLeftIndex:
          svg.children[0].setAttribute(
            "style",
            "transform: translate(-5px, 0px)"
          );
          svg.children[1].setAttribute(
            "style",
            "transform: translate(0px, 5px)"
          );
          break;
        case desktopBottomRightIndex:
          svg.children[0].setAttribute(
            "style",
            "transform: translate(5px, 0px)"
          );
          svg.children[1].setAttribute(
            "style",
            "transform: translate(0px, 5px)"
          );
          break;
        default:
          svg.children[0].setAttribute(
            "style",
            "transform: translate(0px, 0px)"
          );
          svg.children[1].setAttribute(
            "style",
            "transform: translate(0px, 0px)"
          );
          break;
      }
    });

    this.mobileCrosshairSVGs.forEach((svg, i) => {
      switch (i) {
        case mobileTopLeftIndex:
          svg.children[0].setAttribute(
            "style",
            "transform: translate(-5px, 0px)"
          );
          svg.children[1].setAttribute(
            "style",
            "transform: translate(0px, -5px)"
          );
          break;
        case mobileTopRightIndex:
          svg.children[0].setAttribute(
            "style",
            "transform: translate(5px, 0px)"
          );
          svg.children[1].setAttribute(
            "style",
            "transform: translate(0px, -5px)"
          );
          break;
        case mobileBottomLeftIndex:
          svg.children[0].setAttribute(
            "style",
            "transform: translate(-5px, 0px)"
          );
          svg.children[1].setAttribute(
            "style",
            "transform: translate(0px, 5px)"
          );
          break;
        case mobileBottomRightIndex:
          svg.children[0].setAttribute(
            "style",
            "transform: translate(5px, 0px)"
          );
          svg.children[1].setAttribute(
            "style",
            "transform: translate(0px, 5px)"
          );
          break;
        default:
          svg.children[0].setAttribute(
            "style",
            "transform: translate(0px, 0px)"
          );
          svg.children[1].setAttribute(
            "style",
            "transform: translate(0px, 0px)"
          );
          break;
      }
    });
  }

  resetInputCrosshairs = () => {
    this.desktopCrosshairSVGs.forEach((svg) => {
      svg.children[0].setAttribute("style", "transform: translate(0px, 0px)");
      svg.children[1].setAttribute("style", "transform: translate(0px, 0px)");
    });

    this.mobileCrosshairSVGs.forEach((svg) => {
      svg.children[0].setAttribute("style", "transform: translate(0px, 0px)");
      svg.children[1].setAttribute("style", "transform: translate(0px, 0px)");
    });
  };

  showLegalRow() {
    this.legalRow.classList.add("homepage-contest-form__legal-row--active");
    this.legalRow.style.setProperty(
      "max-height",
      `${this.legalRow.scrollHeight}px`
    );

    this.legalRow.addEventListener(
      "transitionend",
      () => {
        this.legalRow.style.setProperty("max-height", "10000px");
      },
      { once: true }
    );
  }
}
